import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Modern kit greenhouse on a 2x3 tile patch: 1.8 m wide (X) by 2.8 m long
// (Z), long axis along Z. Low white base walls, glass walls above, a glass
// gable roof (two panes meeting at a ridge along Z), white frame ribs and a
// glass door on the +Z end.
const FRAME = "#f2f0ea";
const GLASS = "#9fc4d4";

const W = 1.8; // along X
const L = 2.8; // along Z
const BASE_H = 0.4;
const EAVE_Y = 1.6;
const RIDGE_Y = 2.3;
const GLASS_H = EAVE_Y - BASE_H;

// Roof gable fill: a 3-sided cylinder makes a triangular prism; the axis is
// rotated onto Z and the y-scale flattens the section to the roof rise.
const GABLE_R = (W - 0.04) / Math.sqrt(3);
const GABLE_SY = (RIDGE_Y - EAVE_Y) / (1.5 * GABLE_R);

// Roof panes: from the eave (x = +-0.95, y = 1.58) up to the ridge.
const EAVE = { y: EAVE_Y - 0.02, x: 0.95 };
const PANE_ANGLE = Math.atan2(RIDGE_Y + 0.02 - EAVE.y, EAVE.x);
const PANE_LEN = Math.hypot(RIDGE_Y + 0.02 - EAVE.y, EAVE.x) + 0.08;
const PANE_MID: [number, number] = [(EAVE.y + RIDGE_Y + 0.02) / 2, EAVE.x / 2];

export const greenhouse = defineModel({
  id: "greenhouse",
  batchId: "modern",
  name: "Greenhouse",
  tags: ["modern", "outdoor", "garden", "greenhouse", "glass", "plants"],
  summary: "A glass greenhouse with white framing and a gable roof.",
  description:
    "A glass greenhouse of the Modern Living kit on a 2x3 tile patch: 1.8 m " +
    "wide by 2.8 m long with low white base walls, glass walls, a glass " +
    "gable roof with the ridge along Z, white frame ribs and a glass door " +
    "on the +Z end.",
  tiles: { w: 2, h: 3 },
  footprint: 1.9,
  parts(): Part[] {
    const ribs: Part[] = [];
    for (const x of [-0.88, 0.88]) {
      for (const z of [-0.7, 0.7]) {
        ribs.push({
          geometry: new THREE.BoxGeometry(0.05, GLASS_H, 0.05),
          color: FRAME,
          position: [x, BASE_H + GLASS_H / 2, z],
        });
      }
    }
    return [
      // White base walls: two long sides, the -Z end, and two +Z segments
      // flanking the door.
      {
        geometry: new THREE.BoxGeometry(0.08, BASE_H, L),
        color: FRAME,
        position: [-0.86, BASE_H / 2, 0],
      },
      {
        geometry: new THREE.BoxGeometry(0.08, BASE_H, L),
        color: FRAME,
        position: [0.86, BASE_H / 2, 0],
      },
      {
        geometry: new THREE.BoxGeometry(W - 0.16, BASE_H, 0.08),
        color: FRAME,
        position: [0, BASE_H / 2, -(L - 0.08) / 2],
      },
      {
        geometry: new THREE.BoxGeometry(0.47, BASE_H, 0.08),
        color: FRAME,
        position: [-0.585, BASE_H / 2, (L - 0.08) / 2],
      },
      {
        geometry: new THREE.BoxGeometry(0.47, BASE_H, 0.08),
        color: FRAME,
        position: [0.585, BASE_H / 2, (L - 0.08) / 2],
      },
      // Glass walls above the base.
      {
        geometry: new THREE.BoxGeometry(0.05, GLASS_H, L),
        color: GLASS,
        position: [-0.86, BASE_H + GLASS_H / 2, 0],
      },
      {
        geometry: new THREE.BoxGeometry(0.05, GLASS_H, L),
        color: GLASS,
        position: [0.86, BASE_H + GLASS_H / 2, 0],
      },
      {
        geometry: new THREE.BoxGeometry(W - 0.16, GLASS_H, 0.05),
        color: GLASS,
        position: [0, BASE_H + GLASS_H / 2, -(L - 0.05) / 2],
      },
      // +Z end glass panels flanking the door.
      {
        geometry: new THREE.BoxGeometry(0.47, GLASS_H, 0.05),
        color: GLASS,
        position: [-0.585, BASE_H + GLASS_H / 2, (L - 0.05) / 2],
      },
      {
        geometry: new THREE.BoxGeometry(0.47, GLASS_H, 0.05),
        color: GLASS,
        position: [0.585, BASE_H + GLASS_H / 2, (L - 0.05) / 2],
      },
      ...ribs,
      // Glass gable fill closing the roof volume (ridge along Z).
      {
        geometry: new THREE.CylinderGeometry(GABLE_R, GABLE_R, L - 0.1, 3, 1, false, Math.PI)
          .rotateX(Math.PI / 2)
          .scale(1, GABLE_SY, 1),
        color: GLASS,
        position: [0, EAVE_Y + (GABLE_R / 2) * GABLE_SY, 0],
      },
      // Two glass roof panes meeting at the ridge.
      {
        geometry: new THREE.BoxGeometry(PANE_LEN, 0.04, L + 0.15),
        color: GLASS,
        position: [-PANE_MID[1], PANE_MID[0], 0],
        rotation: [0, 0, PANE_ANGLE],
      },
      {
        geometry: new THREE.BoxGeometry(PANE_LEN, 0.04, L + 0.15),
        color: GLASS,
        position: [PANE_MID[1], PANE_MID[0], 0],
        rotation: [0, 0, -PANE_ANGLE],
      },
      // White ridge cap along Z.
      {
        geometry: new THREE.BoxGeometry(0.1, 0.06, L + 0.15),
        color: FRAME,
        position: [0, RIDGE_Y + 0.03, 0],
      },
      // Door on the +Z end: white frame posts + lintel around a glass leaf.
      {
        geometry: new THREE.BoxGeometry(0.06, 1.6, 0.06),
        color: FRAME,
        position: [-0.38, 0.8, L / 2],
      },
      {
        geometry: new THREE.BoxGeometry(0.06, 1.6, 0.06),
        color: FRAME,
        position: [0.38, 0.8, L / 2],
      },
      {
        geometry: new THREE.BoxGeometry(0.82, 0.06, 0.06),
        color: FRAME,
        position: [0, 1.63, L / 2],
      },
      {
        geometry: new THREE.BoxGeometry(0.64, 1.55, 0.04),
        color: GLASS,
        position: [0, 0.775, L / 2],
      },
    ];
  },
  animations: [],
});
